// ./src/utils/bulkWrites.ts
//
// ROADMAP #131 — a bulk action's writes (add guests, assign stays, check in tickets, refund, reorder, remove the
// selected) go out a FEW at a time, and stop once the server stops answering.
//
// ⚠⚠ WHY. `Promise.all(ids.map(save))` fired every request at once: over HTTP/1.1 the browser queues all but six, and a
// time limit that starts when a request is CALLED ran out on requests never sent. A few at a time (@em/utils
// `mapWithConcurrency`), each request's clock starts when it goes out. But on a server that has stopped answering, a few
// at a time is n/4 × the 45 s limit with the drawer locked (100 guests ≈ 19 minutes) — so once ONE write times out, the
// rest are not sent: each comes back as a refusal saying so, and the caller reports them like any other failure.
// ▶ check-request-timeout sweeps that no write fan-out uses `Promise.all` any more.
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { errorMessages, isTimeoutResult } from "@em/api";
import { BULK_CONCURRENCY, mapWithConcurrency } from "@em/utils";

/** What an item that was never sent says: the server had stopped answering, and some may have gone through. */
export const BULK_NOT_SENT_MESSAGE = "Not sent — the server stopped answering. Check what went through before trying the rest again.";

/** Did the server stop answering during this bulk action (a write timed out, so some were not sent)? */
export const isBulkStopped = (results: readonly OpResult[]): boolean =>
  results.some((r) => isTimeoutResult(r) || errorMessages(r).includes(BULK_NOT_SENT_MESSAGE));

/**
 * Run `write` for every item, a few at a time, results in the items' order. Once a write times out, the items not yet
 * started are not sent — each gets a NETWORK_ERROR refusal with `BULK_NOT_SENT_MESSAGE`.
 */
export const runBulkWrites = async <T>(items: readonly T[], write: (item: T, index: number) => Promise<OpResult>): Promise<OpResult[]> => {
  let stopped = false;

  return mapWithConcurrency(items, BULK_CONCURRENCY, async (item, index) => {
    if (stopped) {
      return OpResult.fail(OP_RESULT_CODES.NETWORK_ERROR, null, BULK_NOT_SENT_MESSAGE);
    }

    const result = await write(item, index);
    if (isTimeoutResult(result)) {
      stopped = true;
    }

    return result;
  });
};
